import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import CalendarTimeTrackNav from "@/components/calendar/TimeTrackNav.vue";
import CalendarSettings from "@/components/calendar/Settings.vue";
import ContingentTimeTrackNav from "@/components/contingent/TimeTrackNav.vue";
import ContingentSettings from "@/components/contingent/Settings.vue";
import Contingent from "../views/Contingent.vue";
import PropertiesAndUnits from "../views/PropertiesAndUnits.vue";
import Property from "../views/Property.vue";
import AccomodationUnitType from "../views/AccomodationUnitType.vue";
import RatePlans from "../views/RatePlans.vue";
import Reservations from "../views/Reservations.vue";
import ReservationsMobile from "../views/ReservationsMobile.vue";
import Finances from "../views/Finances.vue";
import Customers from "../views/Customers.vue";
import EVisitors from "../views/EVisitors.vue";
import AuthLayout from "@/views/auth/_AuthLayout.vue";
import Signin from "@/views/auth/Signin.vue";
import ResetPassword from "@/views/auth/ResetPassword.vue";
import ForgottenPassword from "@/views/auth/ForgottenPassword.vue";
import Messages from "@/views/Messages.vue";
import NotificationTemplates from "@/views/Settings/NotificationTemplates.vue";
import NotificationTemplateForm from "@/components/messages/templates/NotificationTemplateForm.vue";
import DocumentSettings from "@/views/Settings/DocumentSettings.vue";
import GuestReviews from "../views/GuestReviews.vue"
import Maintenance from "@/views/Maintenance.vue";
import store from "@/store";

import { policyGuard } from "./policyGuard";

import responsiveRoute from "./layoutSwitcher";
import AppParamsSearch from "@/components/admin/AppParamsSearch.vue";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/signin",
    component: AuthLayout,
    children: [{
      path: "/",
      name: "Signin",
      component: Signin,
      meta: {
        title: 'SigninTo'
      }
    }]
  },
  {
    path: "/reset-password",
    component: AuthLayout,
    children: [{
      path: "/",
      name: "Reset password",
      component: ResetPassword
    }]
  },
  {
    path: "/forgotten-password",
    component: AuthLayout,
    children: [{
      path: "/",
      name: "Forgotten password",
      component: ForgottenPassword
    }]
  },
  {
    path: "/",
    name: "DefaultLayout",
    meta: {
      requiresSignin: true,
    },
    component: responsiveRoute({
      // route level code-splitting
      // this generates a separate chunk ("webpackChunkName".[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      xs: () => import(/* webpackChunkName: "_MobileLayout" */ '../views/_MobileLayout.vue'),
      // sm key is missing, it falls back to xs
      md: () => import(/* webpackChunkName: "_DefaultLayout" */ '../views/_DefaultLayout.vue'),
      // lg, xl keys are missing falls back to md,
    }),
    children: [
      {
        path: "/dashboard",
        name: "Dashboard",
        component: responsiveRoute({
          xs: () => import(/* webpackChunkName: "HomeMobile" */ '../views/HomeMobile.vue'),
          md: () => import(/* webpackChunkName: "Home" */ '../views/Home.vue'),
        }),
        meta: {
          crumbs: [
            { name: "Dashboard" }
          ]
        }
      },
      {
        path: "/calendar",
        name: "Calendar",
        component: responsiveRoute({
          xs: () => import(/* webpackChunkName: "CalendarMobile" */ '../views/CalendarMobile.vue'),
          md: () => import(/* webpackChunkName: "Calendar" */ '../views/Calendar.vue'),
        }),
        meta: {
          policy: "READ_CALENDAR",
          crumbs: [
            { name: "Calendar" }
          ]
        },
        components: {
          default: responsiveRoute({
            xs: () => import(/* webpackChunkName: "CalendarMobile" */ '../views/CalendarMobile.vue'),
            md: () => import(/* webpackChunkName: "Calendar" */ '../views/Calendar.vue'),
          }),
          header: CalendarTimeTrackNav,
          menu: CalendarSettings
        }
      },
      {
        path: "/contingent",
        name: "Contingent",
        component: Contingent,
        meta: {
          policy: "READ_CALENDAR",
          crumbs: [
            { name: "Contingent" }
          ]
        },
        components: {
          default: Contingent,
          header: ContingentTimeTrackNav,
          menu: ContingentSettings
        }
      },
      {
        path: "/reservations",
        name: "Reservations",
        component: responsiveRoute({
          xs: () => import(/* webpackChunkName: "ReservationsMobile" */ '../views/ReservationsMobile.vue'),
          md: () => import(/* webpackChunkName: "Reservations" */ '../views/Reservations.vue'),
        }),
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "/reservations",
          datePicker: false,
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Customers.Reservations",
        component: Reservations,
        meta: {
          policy: "READ_RESERVATIONS",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Reservations.New",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/NewReservations",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Reservations.Modified",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "/dashboard/ModifiedReservations",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Reservations.Canceled",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "/dashboard/CanceledReservations",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Arrivals.Pending",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Arrivals",
          completedFilter: "0",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Arrivals.Done",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Arrivals",
          completedFilter: "1",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "AllArrivals",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Arrivals",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "AllDepartures",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Departures",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Departures.Pending",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Departures",
          completedFilter: "0",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Departures.Done",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/Departures",
          completedFilter: "1",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.Occupancy",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/occupancy",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/reservations",
        name: "Dashboard.CurrentStays",
        component: ReservationsMobile,
        meta: {
          policy: "READ_RESERVATIONS",
          endpoint: "dashboard/CurrentStays",
          crumbs: [
            { name: "Reservations" }
          ]
        },
      },
      {
        path: "/financial-documents",
        name: "Financial documents",
        component: Finances,
        meta: {
          policy: "READ_FINANCIAL_DOCUMENTS",
          crumbs: [
            { name: "Financial documents" }
          ]
        },
      },
      {
        path: "/customers",
        name: "Customers",
        component: Customers,
        meta: {
          policy: "READ_CUSTOMERS_GRID",
          crumbs: [
            { name: "Customers" }
          ]
        },
      },
      {
        path: "/evisitors",
        name: "EVisitors",
        component: EVisitors,
        meta: {
          policy: "MODIFY_RESERVATIONS",
          crumbs: [
            { name: "EVisitors" }
          ]
        },
      },
      {
        path: "/guest-reviews",
        name: "GuestReviews",
        component: GuestReviews,
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Guest reviews" }
          ]
        }
      },
      {
        path: "/properties",
        name: "Properties and units",
        component: PropertiesAndUnits,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Properties and units" }
          ]
        }
      },
      {
        path: "/properties/new",
        name: "New property",
        component: Property,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Properties and units", link: "/properties" },
            { name: "New property" }
          ]
        },
      },
      {
        path: "/properties/:id(\\d+)",
        name: "Edit property",
        component: Property,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Properties and units", link: "/properties" },
            { name: "Edit property" }
          ]
        },
      },

      {
        path: "/properties/:propertyId(\\d+)/unittypes/new",
        name: "New unit type",
        component: AccomodationUnitType,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Properties and units", link: "/properties" },
            { name: "Edit property", link: "/properties/:propertyId" },
            { name: "New unit type" }
          ]
        }
      },
      {
        path: "/properties/:propertyId(\\d+)/unittypes/:unitTypeId(\\d+)",
        name: "Edit unit type",
        component: AccomodationUnitType,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Properties and units", link: "/properties" },
            { name: "Edit property", link: "/properties/:propertyId" },
            { name: "Edit unit type" }
          ]
        }
      },

      {
        path: "/properties/*",
        redirect: "/properties"
      },
      {
        path: "/rate-plans",
        name: "Rate plans",
        component: RatePlans,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Rate plans" }
          ]
        }
      },
      {
        path: "/reports",
        name: "Reports",
        component: responsiveRoute({
          xs: () => import(/* webpackChunkName: "ReportsMobile" */ '../views/ReportsMobile.vue'),
          md: () => import(/* webpackChunkName: "Reports" */ '../views/Reports.vue'),
        }),
        meta: {
          policy: "READ_REPORTS",
          datePicker: false,
          crumbs: [
            { name: "Reports" }
          ]
        }
      },
      {
        path: "/settings",
        name: "Settings",
        component: () => import("../views/Settings/Settings.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings" }
          ]
        }
      },
      {
        path: "/settings/users",
        name: "Users",
        component: () => import("../views/Settings/Users.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "Users" }
          ]
        }
      },
      {
        path: "/settings/add-on-services-and-fees",
        name: "ServicesAndFees",
        component: () => import("../views/Settings/AddOnServicesAndFees.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "ServicesAndFees" }
          ]
        }
      },
      {
        path: "/settings/agency-fees",
        name: "AgencyFees",
        component: () => import("../views/Settings/AgencyFees.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "AgencyFees" }
          ]
        }
      },
      {
        path: "/settings/financial",
        name: "FinancialSettings",
        component: () => import("../views/Settings/Financial.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "FinancialSettings" }
          ]
        }
      },
      {
        path: "/settings/sales-channels",
        name: "SalesChannels",
        component: () => import("../views/Settings/SalesChannels.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "SalesChannels" }
          ]
        }
      },
      {
        path: "/settings/service-types",
        name: "ServiceTypes",
        component: () => import("../views/Settings/ServiceTypes.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "ServiceTypes" }
          ]
        }
      },
      {
        path: "/settings/tax-rates",
        name: "CityTaxRate",
        component: () => import("../views/Settings/CityTaxRates.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "CityTaxRate" }
          ]
        }
      },
      {
        path: "/settings/demand-changing-events",
        name: "DemandChangingEvents",
        component: () => import("../views/Settings/DemandChangingEvents.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "DemandChangingEvents" }
          ]
        }
      },
      {
        path: "/messages",
        name: "Messages",
        component: Messages,
        meta: {
          policy: "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Messages" }
          ]
        }
      },
      {
        path: "/settings/notification-templates",
        name: "NotificationTemplates",
        component: NotificationTemplates,
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "NotificationTemplates" }
          ]
        }
      },
      {
        path: "/settings/notification-templates/new",
        name: "NotificationTemplateNew",
        component: NotificationTemplateForm,
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "NotificationTemplates", link: "/settings/notification-templates" },
            { name: "NotificationTemplateNew" }
          ]
        }
      },
      {
        path: "/settings/notification-templates/:id(\\d+)",
        name: "NotificationTemplateEdit",
        component: NotificationTemplateForm,
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "NotificationTemplates", link: "/settings/notification-templates" },
            { name: "NotificationTemplateEdit" }
          ]
        }
      },
      {
        path: "/settings/document-settings/",
        name: "DocumentSettings",
        component: DocumentSettings,
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "DocumentSettings" }
          ]
        }
      },
      {
        path: "/maintenance",
        name: "Maintenance",
        component: Maintenance,
        meta: {
          crumbs: [
            { name: "Maintenance" }
          ]
        }
      },
      {
        path: "/settings/api-credentials",
        name: "ApiCredentials",
        component: () => import("../views/Settings/ApiCredentials.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "ApiCredentials" }
          ]
        }
      },
      {
        path: "/settings/los-category-templates",
        name: "LOSCategoryTemplates",
        component: () => import("../views/Settings/LOSCategoryTemplates.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "LOSCategoryTemplate" }
          ]
        }
      },
      {
        path: "/settings/ob-category-templates",
        name: "OBCategoryTemplates",
        component: () => import("../views/Settings/OBCategoryTemplates.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "OBCategoryTemplate" }
          ]
        }
      },
      {
        path: "/settings/business-units",
        name: "BusinessUnits",
        component: () => import("../views/Settings/BusinessUnits.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "BusinessUnits" }
          ]
        }
      },
      {
        path: "/settings/deposit-withdrawal-codebook",
        name: "TransactionDescriptionCodebook",
        component: () => import("../views/Settings/DepositWithdrawalCodebook.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "TransactionDescriptionCodebook" }
          ]
        }
      },
      {
        path: "/settings/cash-registers",
        name: "CashRegisters",
        component: () => import("../views/Settings/CashRegisters.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "CashRegisters" }
          ]
        }
      },
      {
        path: "/settings/application-appearance",
        name: "DistributionChannelAppearance",
        component: () => import("../views/Settings/ApplicationAppearance.vue"),
        meta: {
          policy: "REQUIRE_TENANT_ADMIN_OR_HIGHER",
          crumbs: [
            { name: "Settings", link: "/settings" },
            { name: "DistributionChannelAppearance" }
          ]
        }
      },
      {
        path: "/admin",
        name: "Admin",
        component: () => import("../views/SysAdminSettings/SysAdminSettings.vue"),
        meta: {
          policy: "REQUIRE_SYS_ADMIN",
          crumbs: [
            { name: "Admin" }
          ]
        }
      },
      {
        path: "/admin/app-params",
        name: "AppParamsSettings",
        components: {
          default: () => import("../views/SysAdminSettings/AppParamsSettings.vue"),
          'header-left': AppParamsSearch,
        },
        meta: {
          policy: "REQUIRE_SYS_ADMIN",
          crumbs: [
            { name: "Admin", link: "/admin" },
            { name: "AppParamsSettings" }
          ]
        }
      },
      {
        path: "/admin/tenant/new",
        name: "TenantCopySettings",
        component: () => import("../views/SysAdminSettings/TenantCopySettings.vue"),
        meta: {
          policy: "REQUIRE_SYS_ADMIN",
          crumbs: [
            { name: "Admin", link: "/admin" },
            { name: "TenantCopySettings" }
          ]
        }
      },
      {
        path: "**",
        redirect: "/dashboard",
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});


// authguard
router.beforeEach((to, from, next) => {
  if (to.matched.some((x) => x.meta.requiresSignin)) {
    // this timeout means app has 5s to aquire a token
    const timeout = new Promise<void>((x) => setTimeout(() => x(), 5000));

    Promise.race([timeout, store.getters["auth/ready"]]).then(() => {
      if (!store.getters["auth/isSignedIn"]) {
        next({ name: "Signin" });
      } else {
        policyGuard(to, next);
      }
    });
  } else {
    next();
  }
});

export default router;
